import {Box, Grid, useTheme} from '@mui/material';
import AppInfiniteScrollContainer from 'components/AppInfiniteScrollContainer';
import AppIconButton from 'components/buttons/AppIconButton';
import {BaseCard} from 'components/card';
import AppBaseCardHeader from 'components/card/AppBaseCardHeader';
import {AppConfirmationDialog, AppConfirmationDialogRefType} from 'components/dialogs';
import {AppDrawer} from 'components/drawers';
import {AppMenu} from 'components/menus';
import {AppTooltip} from 'components/toolip';
import {AppEllipsisTypography} from 'components/typographies';
import {extractFirstElementText} from 'global';
import {useGetAllNotes, useRemoveNote} from 'hooks';
import parser from 'html-react-parser';
import {Edit, Trash} from 'iconsax-react';
import {NoteResponse} from 'models/notes';
import {useEffect, useRef, useState} from 'react';
import {FcSurvey} from 'react-icons/fc';
import {HiPlus} from 'react-icons/hi';
import {useSelector} from 'react-redux';
import {appDrawerSelector} from 'store/themeSettings';
import {DrawerTypeValue} from 'types';
import NotesAddUpdateDialog from './NotesAddUpdateDialiog';
export const NoteDrawer: React.FC = () => {
    const {getAllNotes, getAllNotesResponse, afterCreateUpdateHandler, removeItemState} = useGetAllNotes();
    const noteRemoveConfirmationDialiogRef = useRef<AppConfirmationDialogRefType>(null);
    const nodeViewConfirmationDialiogRef = useRef<AppConfirmationDialogRefType>(null);
    const {removeNote, removeNoteLoading, removeNoteResponse} = useRemoveNote();
    const [selectedNote, setSelectedNote] = useState<string | undefined>();
    const {isRightDrawerOpened} = useSelector(appDrawerSelector);

    useEffect(() => {
        if (removeNoteResponse && !removeNoteLoading) {
            removeItemState(removeNoteResponse.data);
        }
    }, [removeNoteLoading, removeNoteResponse]);

    const theme = useTheme();

    const onUpdateClickHandler = (id?: string) => {
        if (!id) return;
        setSelectedNote(id);
        setOpenAddEditDialog(true);
    };

    useEffect(() => {
        getAllNotes({
            variables: {
                first: 20,
            },
        });
    }, []);

    const handleNext = () => {
        getAllNotes({
            variables: {
                first: 12,
                after: getAllNotesResponse?.pageInfo.endCursor,
            },
        });
    };
    const [openAddEditDialog, setOpenAddEditDialog] = useState(false);
    const handleCloseAddEditDialig = (value?: NoteResponse) => {
        if (value) {
            afterCreateUpdateHandler(value);
        }
        setOpenAddEditDialog(false);
    };
    const handleNoteView = (id?: string) => {
        if (nodeViewConfirmationDialiogRef.current && id) {
            setSelectedNote(id);
            nodeViewConfirmationDialiogRef.current?.handleOpen();
        }
    };
    const handleOpenAddEditDialig = () => {
        setSelectedNote(undefined);
        setOpenAddEditDialog(true);
    };
    return (
        <>
            <AppDrawer
                drawerType={DrawerTypeValue.Note}
                renderElement={(_open, handleOpen) => {
                    return (
                        <Box sx={{flexShrink: 0, ml: 0.75}}>
                            <AppTooltip title="Notes" placement="left-start">
                                <AppIconButton
                                    elevation={DrawerTypeValue.Note === isRightDrawerOpened}
                                    onClick={handleOpen}
                                >
                                    <FcSurvey />
                                </AppIconButton>
                            </AppTooltip>
                        </Box>
                    );
                }}
                title="Notes"
                showHeaderButton
                nodeBeforeTitle={
                    <AppIconButton color="primary" variant="outlined" onClick={handleOpenAddEditDialig}>
                        <HiPlus />
                    </AppIconButton>
                }
            >
                <AppInfiniteScrollContainer data={getAllNotesResponse} next={handleNext}>
                    <Grid container>
                        {getAllNotesResponse?.nodes.map(data => {
                            return (
                                <Grid item xs={12} key={data.id}>
                                    <BaseCard
                                        cardProps={{
                                            hoverEffect: true,
                                            onClick: event => {
                                                event.stopPropagation();
                                                handleNoteView(data.id);
                                            },
                                        }}
                                        headerData={
                                            <AppBaseCardHeader title={data.title} dateTime={data.createdDate} />
                                        }
                                        key={data.id}
                                        menus={
                                            <AppMenu
                                                menuItems={[
                                                    {
                                                        label: 'Edit',
                                                        icon: (
                                                            <Box mt={1}>
                                                                <Edit
                                                                    color={theme.palette.primary.main}
                                                                    variant="Outline"
                                                                    size={'16px'}
                                                                />
                                                            </Box>
                                                        ),
                                                        onClick: () => onUpdateClickHandler(data.id),
                                                    },
                                                    {
                                                        label: 'Delete',
                                                        icon: (
                                                            <Box mt={1}>
                                                                <Trash
                                                                    color={theme.palette.error.main}
                                                                    variant="Bold"
                                                                    size={'16px'}
                                                                />
                                                            </Box>
                                                        ),
                                                        onClick: () => {
                                                            if (noteRemoveConfirmationDialiogRef.current) {
                                                                setSelectedNote(data.id);
                                                                noteRemoveConfirmationDialiogRef.current?.handleOpen();
                                                            }
                                                        },
                                                    },
                                                ]}
                                            />
                                        }
                                        footerData={
                                            <Box
                                                sx={{flexGrow: 1, display: 'column', alignItems: 'center'}}
                                                onClick={() => handleNoteView(data.id)}
                                            >
                                                {extractFirstElementText(data.description ?? '').length > 0 && (
                                                    <AppEllipsisTypography
                                                        variant="body2"
                                                        hideDetails
                                                        whiteSpace="normal"
                                                        text={extractFirstElementText(data.description ?? '')}
                                                        maxLetters={150}
                                                    />
                                                )}
                                            </Box>
                                        }
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </AppInfiniteScrollContainer>
            </AppDrawer>
            {openAddEditDialog && (
                <NotesAddUpdateDialog
                    noteId={selectedNote}
                    open={openAddEditDialog}
                    onCloseDialog={handleCloseAddEditDialig}
                    afterCreateUpdateHandler={afterCreateUpdateHandler}
                />
            )}
            <AppConfirmationDialog
                maxWidth="sm"
                renderElement={(_open, _handleOpen) => <></>}
                ref={nodeViewConfirmationDialiogRef}
                alignContainer="left"
                title={getAllNotesResponse?.nodes.find(n => n.id === selectedNote)?.title ?? ''}
                description={
                    selectedNote
                        ? parser(getAllNotesResponse?.nodes.find(n => n.id === selectedNote)?.description ?? '')
                        : ''
                }
                footer={<></>}
            />
            <AppConfirmationDialog
                ref={noteRemoveConfirmationDialiogRef}
                renderElement={(_open, handleOpen) => <></>}
                icon={<Trash variant="Bold" />}
                onConfirmClick={(_open, _handleOpen, handleClose) => {
                    if (selectedNote) removeNote(selectedNote);
                    handleClose();
                }}
                title="Delete Note"
                description="Are you sure you want to delete this note?"
            />
        </>
    );
};

export default NoteDrawer;
